/* ClientAutocomplete — vyhledávací input s našeptáváním klientů.
 *
 * Vstup: píšu jméno/telefon/email → debounce 200ms → fetch /api/crm/contacts
 * (lokálně filtruje cached seznam). Výběr → onChange(id, contact).
 * Filtruje out test/duplicate test záznamy (TEST, Test, lowercased).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Search, X, User as UserIcon, Phone, Mail, Loader2, Plus } from "lucide-react";
import api from "@/lib/api";

let _cache = null;        // { ts, items }
let _inflight = null;

async function fetchContactsCached() {
  if (_cache && (Date.now() - _cache.ts) < 60_000) return _cache.items;
  if (_inflight) return _inflight;
  _inflight = api.get("/api/crm/contacts")
    .then((r) => {
      _cache = { ts: Date.now(), items: Array.isArray(r.data) ? r.data : [] };
      _inflight = null;
      return _cache.items;
    })
    .catch(() => { _inflight = null; return []; });
  return _inflight;
}

export function invalidateClientCache() { _cache = null; }

// Kontakty mají name (křestní) + surname zvlášť — zobrazuj VŽDY celé jméno.
export { contactFullName } from "@/lib/contactName";
import { contactFullName } from "@/lib/contactName";

export default function ClientAutocomplete({
  value,
  onChange,
  testid = "client-autocomplete",
  placeholder = "Hledej klienta (jméno, telefon, e-mail) …",
  allowClear = true,
  allowCreate = false,
  onCreateNew,
  selectedObj = null,
  autoFocus = false,
}) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    fetchContactsCached().then((it) => {
      if (!mounted) return;
      setItems(it);
      setLoading(false);
    });
    return () => { mounted = false; };
  }, []);

  // Resolve current selected contact (for display when value is set)
  const selected = useMemo(
    () => items.find((c) => c.id === value) || (value && selectedObj && selectedObj.id === value ? selectedObj : null),
    [items, value, selectedObj]
  );

  // Synthetic display string when input not focused (selected → celé jméno)
  const [display, setDisplay] = useState("");
  useEffect(() => {
    if (selected && !open) setDisplay(contactFullName(selected));
    else if (!selected && !open) setDisplay("");
  }, [selected, open]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) {
      // No query → top 8 by activity (sign_date desc → updated_at desc)
      return items
        .filter((c) => !/^test\s|^test$/i.test(c.name || ""))
        .slice(0, 8);
    }
    const scored = [];
    for (const c of items) {
      const name = `${c.name || ""} ${c.surname || ""}`.trim().toLowerCase();
      const phones = [c.phone, c.mobile, c.phone_work, c.phone_home].filter(Boolean).join(" ").toLowerCase();
      const email = (c.email || "").toLowerCase();
      const ico = (c.ico || "").toLowerCase();
      let score = 0;
      if (name.startsWith(term)) score += 100;
      else if (name.includes(term)) score += 50;
      if (phones.includes(term)) score += 60;
      if (email.includes(term)) score += 50;
      if (ico.includes(term)) score += 40;
      if (score > 0) scored.push({ c, score });
    }
    scored.sort((a, b) => b.score - a.score);
    return scored.slice(0, 12).map((x) => x.c);
  }, [items, q]);

  useEffect(() => { setHi(0); }, [q]);

  // Close on outside click
  useEffect(() => {
    const h = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const pick = (c) => {
    onChange?.(c?.id || "", c);
    setDisplay(contactFullName(c));
    setQ("");
    setOpen(false);
  };

  const clear = () => {
    onChange?.("", null);
    setQ("");
    setDisplay("");
  };

  const onKey = (e) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") { setOpen(true); e.preventDefault(); }
      return;
    }
    // dropdown otevřený → Enter/Esc/šipky neprobublají do rodiče (modal)
    if (["ArrowDown", "ArrowUp", "Enter", "Escape"].includes(e.key)) e.stopPropagation();
    if (e.key === "ArrowDown") { setHi((h) => Math.min(filtered.length - 1, h + 1)); e.preventDefault(); }
    else if (e.key === "ArrowUp") { setHi((h) => Math.max(0, h - 1)); e.preventDefault(); }
    else if (e.key === "Enter") {
      if (filtered[hi]) { pick(filtered[hi]); e.preventDefault(); }
    } else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={wrapRef} className="relative" data-testid={testid}>
      <div className="relative">
        <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--muted)" }} />
        <input
          value={open ? q : (selected ? display : q)}
          onChange={(e) => { setQ(e.target.value); setOpen(true); if (selected && e.target.value !== display) { /* user typing over selection */ } }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder={placeholder}
          autoFocus={autoFocus}
          data-testid={`${testid}-input`}
          className="w-full rounded-md px-2 py-1.5 pl-7 pr-7 text-maly outline-none"
          style={{ background: "var(--bg)", border: "1px solid var(--line)", color: "var(--ink)", height: 32, lineHeight: 1.4 }}
        />
        {loading && (
          <Loader2 size={12} className="absolute right-2 top-1/2 -translate-y-1/2 animate-spin" style={{ color: "var(--muted)" }} />
        )}
        {!loading && (value || q) && allowClear && (
          <button
            type="button"
            onClick={clear}
            data-testid={`${testid}-clear`}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 w-5 h-5 rounded flex items-center justify-center hover:bg-black/5"
            aria-label="Vymazat"
          >
            <X size={11} style={{ color: "var(--muted)" }} />
          </button>
        )}
      </div>
      {open && (
        <div
          className="absolute left-0 right-0 mt-1 rounded-lg overflow-hidden z-50"
          style={{
            background: "var(--surface)", border: "1px solid var(--line)",
            boxShadow: "0 12px 32px rgba(40, 25, 15, 0.15), 0 2px 6px rgba(40, 25, 15, 0.06)",
            maxHeight: 280, overflowY: "auto",
          }}
          data-testid={`${testid}-dropdown`}
        >
          {filtered.length === 0 && (
            <div className="px-3 py-2 text-drobny" style={{ color: "var(--muted)" }}>
              {q.trim() ? `Nic nenalezeno pro „${q}"` : "Žádní klienti zatím nejsou"}
            </div>
          )}
          {filtered.map((c, i) => (
            <button
              key={c.id}
              type="button"
              onClick={() => pick(c)}
              onMouseEnter={() => setHi(i)}
              data-testid={`${testid}-item-${c.id}`}
              className="w-full text-left px-2.5 py-1.5 text-[12px] flex items-center gap-2 transition-colors"
              style={{ background: hi === i ? "var(--accent-soft)" : "transparent", color: "var(--ink)" }}
            >
              <UserIcon size={11} style={{ color: "var(--accent)", flexShrink: 0 }} />
              <div className="flex-1 min-w-0">
                <div className="truncate font-medium">{contactFullName(c) || "—"}</div>
                <div className="text-drobny flex items-center gap-2 truncate" style={{ color: "var(--muted)" }}>
                  {c.email && <span className="inline-flex items-center gap-0.5"><Mail size={9} />{c.email}</span>}
                  {(c.phone || c.mobile) && <span className="inline-flex items-center gap-0.5"><Phone size={9} />{c.phone || c.mobile}</span>}
                </div>
              </div>
              {c.category && (
                <span className="text-popisek uppercase font-bold px-1.5 py-0.5 rounded flex-shrink-0" style={{ background: "var(--bg)", color: "var(--muted)" }}>
                  {c.category}
                </span>
              )}
            </button>
          ))}
          {/* „Vytvořit nového klienta" je STICKY na dně seznamu. Dřív byl až pod
              posledním výsledkem — u poradce s desítkami klientů zmizel pod okrajem
              a vypadalo to, že klienta z obchodu založit nejde (feedback 17. 8. 2026). */}
          {allowCreate && onCreateNew && (
            <button
              type="button"
              onClick={() => { onCreateNew?.(q); setOpen(false); }}
              data-testid={`${testid}-create`}
              className="sticky bottom-0 w-full text-left px-2.5 py-2 text-[12px] flex items-center gap-2 transition-colors hover:bg-black/5"
              style={{ borderTop: "1px solid var(--line)", color: "var(--accent)", fontWeight: 600, background: "var(--surface, #FFFFFF)" }}
            >
              <Plus size={11} /> Vytvořit nového klienta{q.trim() ? ` „${q.trim()}"` : ""}…
            </button>
          )}
        </div>
      )}
    </div>
  );
}
